import * as React from "react";
import { BarChart } from "@mui/x-charts/BarChart";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CircularProgress,
  useTheme,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Stack
} from "@mui/material";
import { axiosApi } from "../../../interceptors/axios";
import { Result } from "../../../types/Result";

interface UserPreferencePeriodStatistic {
  assignmentPeriodCode: string;
  year: number;
  period: number;
  isActive: boolean;
  usersCount: number;
}

interface Profession {
  professionCode: string;
  name: string;
}

export default function UserPreferencePeriodStats(): JSX.Element {
  const theme = useTheme();
  const [stats, setStats] = React.useState<UserPreferencePeriodStatistic[]>([]);
  const [professions, setProfessions] = React.useState<Profession[]>([]);
  const [selectedProfession, setSelectedProfession] = React.useState<string>("");
  const [statsLoading, setStatsLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    const fetchProfessions = async () => {
      try {
        const response = await axiosApi.get<Result<Profession[]>>("api/professions");
        if (response.data && response.data.isSuccess && response.data.data) {
          setProfessions(response.data.data);
        }
      } catch (err) {
        console.error("Error fetching professions:", err);
      }
    };
    fetchProfessions();
  }, []);

  React.useEffect(() => {
    const fetchStats = async () => {
      setStatsLoading(true);
      setError(null);
      try {
        const url = `api/userPreferences/period-statistics${selectedProfession ? `?professionCode=${selectedProfession}` : ""}`;
        const response = await axiosApi.get<Result<UserPreferencePeriodStatistic[]>>(url);
        if (response.data && response.data.isSuccess && response.data.data) {
          setStats(response.data.data);
        } else {
          setError("Veriler alınamadı");
        }
      } catch (err) {
        console.error("Error fetching period statistics:", err);
        setError("Sunucu hatası");
      } finally {
        setStatsLoading(false);
      }
    };

    fetchStats();
  }, [selectedProfession]);

  const handleProfessionChange = (event: SelectChangeEvent) => {
    setSelectedProfession(event.target.value);
  };

  const dataset = stats.map((item) => ({
    label: `${item.year}/${item.period}${item.isActive ? " (Aktif)" : ""}`,
    usersCount: item.usersCount,
  }));

  return (
    <Card variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Typography component="h2" variant="subtitle2" color="primary">
            Atama Dönemlerine Göre Tercih Yapan Kullanıcı Sayısı
          </Typography>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel id="period-profession-select-label">Branş Seçin</InputLabel>
            <Select
              labelId="period-profession-select-label"
              id="period-profession-select"
              value={selectedProfession}
              label="Branş Seçin"
              onChange={handleProfessionChange}
            >
              <MenuItem value="">
                <em>Varsayılan</em>
              </MenuItem>
              {professions.map((prof) => (
                <MenuItem key={prof.professionCode} value={prof.professionCode}>
                  {prof.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Stack>

        <Box sx={{ height: 248, width: "100%", display: 'flex', justifyContent: 'center', position: 'relative' }}>
          {statsLoading && (
            <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1, bgcolor: 'rgba(255,255,255,0.7)', p: 1, borderRadius: 1 }}>
              <CircularProgress size={20} />
            </Box>
          )}

          {error ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="error">{error}</Typography>
            </Box>
          ) : dataset.length > 0 ? (
            <BarChart
              dataset={dataset}
              xAxis={[{ scaleType: "band", dataKey: "label", tickLabelStyle: { fontSize: 12 } }]}
              yAxis={[{ tickMinStep: 1 }]}
              series={[
                {
                  dataKey: "usersCount",
                  label: "Tercih Yapan Kullanıcı",
                  color: theme.palette.primary.main,
                  valueFormatter: (value) => value?.toString() || "0",
                },
              ]}
              barLabel="value"
              hideLegend
              height={248}
              margin={{ left: 10, right: 20, top: 20, bottom: 10 }}
            />
          ) : !statsLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Atama dönemi bulunamadı
              </Typography>
            </Box>
          ) : null}
        </Box>
      </CardContent>
    </Card>
  );
}
